/**
 * Pagina dettaglio lotto di magazzino.
 *
 * Mostra i dati completi del lotto con nomi risolti per articolo,
 * fornitore e posizione. In basso, la tabella movimenti filtrata
 * per il lotto corrente.
 *
 * Importa esclusivamente da @floraltech/ui (regola 6.8 CLAUDE.md).
 */

import {
  Badge,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from '@floraltech/ui';
import React, { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useFormatters } from '@lib/formatters';
import MovimentiTable from '../components/MovimentiTable';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface LottoData {
  id: string;
  tenant_id: string;
  articolo_id: string;
  posizione_id: string | null;
  numero_lotto: string | null;
  quantita_iniziale: number;
  quantita_disponibile: number;
  costo_unitario: number | null;
  data_ingresso: string;
  data_produzione: string | null;
  data_scadenza: string | null;
  fornitore_id: string | null;
  catalogo_fornitore_id: string | null;
  stato: string;
  note: string | null;
  created_at: string;
  updated_at: string;
}

interface ArticoloInfo {
  sku_interno: string;
  descrizione: string | null;
}

interface FornitoreInfo {
  ragione_sociale: string;
}

interface PosizioneInfo {
  codice: string;
  descrizione: string | null;
}

/** Soglia in giorni sotto la quale il lotto e' considerato prossimo a scadenza. */
const GIORNI_SCADENZA_WARNING = 30;

const AUTH_HEADERS = (): Record<string, string> => ({
  Authorization: `Bearer ${localStorage.getItem('token') ?? ''}`,
});

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/**
 * Calcola i giorni rimanenti alla scadenza a partire dalla data odierna.
 *
 * @param dataScadenza - Data di scadenza in formato ISO
 * @returns Numero di giorni rimanenti (negativo se scaduto)
 */
const calcolaGiorniRimanenti = (dataScadenza: string): number => {
  const oggi = new Date();
  oggi.setHours(0, 0, 0, 0);
  const scadenza = new Date(dataScadenza);
  scadenza.setHours(0, 0, 0, 0);
  const diffMs = scadenza.getTime() - oggi.getTime();
  const MS_PER_DAY = 86400000;
  return Math.ceil(diffMs / MS_PER_DAY);
};

/**
 * Determina la variante del Badge scadenza in base ai giorni rimanenti.
 *
 * @param dataScadenza - Data di scadenza in formato ISO
 * @returns La variante del Badge: destructive (scaduto), secondary (<30gg), outline (ok)
 */
const scadenzaVariant = (
  dataScadenza: string,
): 'destructive' | 'secondary' | 'outline' => {
  const giorni = calcolaGiorniRimanenti(dataScadenza);
  if (giorni <= 0) return 'destructive';
  if (giorni < GIORNI_SCADENZA_WARNING) return 'secondary';
  return 'outline';
};

/**
 * Label leggibile per lo stato del lotto.
 *
 * @param stato - Stato grezzo dal backend
 * @returns Label formattata
 */
const statoLabel = (stato: string): string => {
  if (stato === 'disponibile') return 'Disponibile';
  if (stato === 'in_quarantena') return 'In quarantena';
  if (stato === 'esaurito') return 'Esaurito';
  if (stato === 'bloccato') return 'Bloccato';
  return stato;
};

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

const LottoDetail: React.FC = () => {
  const { lottoId } = useParams<{ lottoId: string }>();
  const navigate = useNavigate();
  const fmt = useFormatters();

  const [lotto, setLotto] = useState<LottoData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Nomi risolti
  const [articoloInfo, setArticoloInfo] = useState<ArticoloInfo | null>(null);
  const [fornitoreNome, setFornitoreNome] = useState<string>('');
  const [posizioneLabel, setPosizioneLabel] = useState<string>('');

  // -----------------------------------------------------------------------
  // Load data
  // -----------------------------------------------------------------------

  const caricaLotto = useCallback(async () => {
    if (!lottoId) return;
    setLoading(true);
    setError(null);
    try {
      const response = await fetch(`/api/v1/magazzino/lotti/${lottoId}`, {
        headers: AUTH_HEADERS(),
      });
      if (!response.ok) throw new Error(`Errore ${response.status}`);
      const data: LottoData = await response.json();
      setLotto(data);

      // Risolvi articolo
      try {
        const aResp = await fetch(
          `/api/v1/magazzino/articoli/${data.articolo_id}`,
          { headers: AUTH_HEADERS() },
        );
        if (aResp.ok) {
          const aData = await aResp.json();
          setArticoloInfo({
            sku_interno: aData.sku_interno,
            descrizione: aData.descrizione ?? null,
          });
        }
      } catch { /* ignore */ }

      // Risolvi fornitore
      if (data.fornitore_id) {
        try {
          const fResp = await fetch(
            `/api/v1/soggetti/${data.fornitore_id}`,
            { headers: AUTH_HEADERS() },
          );
          if (fResp.ok) {
            const fData: FornitoreInfo = await fResp.json();
            setFornitoreNome(fData.ragione_sociale ?? '');
          }
        } catch { /* ignore */ }
      }

      // Risolvi posizione
      if (data.posizione_id) {
        try {
          const pResp = await fetch(
            `/api/v1/magazzino/posizioni/${data.posizione_id}`,
            { headers: AUTH_HEADERS() },
          );
          if (pResp.ok) {
            const pData: PosizioneInfo = await pResp.json();
            setPosizioneLabel(
              pData.descrizione
                ? `${pData.codice} — ${pData.descrizione}`
                : pData.codice,
            );
          }
        } catch { /* ignore */ }
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Errore sconosciuto');
    } finally {
      setLoading(false);
    }
  }, [lottoId]);

  useEffect(() => {
    void caricaLotto();
  }, [caricaLotto]);

  // -----------------------------------------------------------------------
  // Render
  // -----------------------------------------------------------------------

  if (loading) {
    return (
      <p className="p-6 text-center text-sm text-muted-foreground">
        Caricamento…
      </p>
    );
  }

  if (error || !lotto) {
    return (
      <p className="p-6 text-center text-sm text-destructive">
        {error ?? 'Lotto non trovato'}
      </p>
    );
  }

  return (
    <div className="space-y-6 p-6">
      {/* Intestazione */}
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="sm" onClick={() => navigate(-1)}>
          ← Indietro
        </Button>
        <h1 className="font-outfit text-2xl font-semibold text-neutral-dark dark:text-neutral-light">
          Lotto {lotto.numero_lotto ?? lotto.id.slice(0, 8)}
        </h1>
        <Badge
          variant={
            lotto.stato === 'disponibile' ? 'default' :
            lotto.stato === 'in_quarantena' ? 'destructive' : 'secondary'
          }
        >
          {statoLabel(lotto.stato)}
        </Badge>
      </div>

      {/* Dettagli lotto */}
      <Card>
        <CardHeader>
          <CardTitle>Dettagli lotto</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 gap-4 text-sm md:grid-cols-4">
            {/* Articolo */}
            <div>
              <p className="text-muted-foreground">Articolo</p>
              {articoloInfo ? (
                <Link
                  to={`/magazzino/articoli/${lotto.articolo_id}`}
                  className="font-medium text-primary hover:underline"
                >
                  {articoloInfo.sku_interno}
                  {articoloInfo.descrizione && ` — ${articoloInfo.descrizione}`}
                </Link>
              ) : (
                <p className="font-medium">—</p>
              )}
            </div>

            {/* Numero lotto */}
            <div>
              <p className="text-muted-foreground">Numero lotto</p>
              <p className="font-mono font-medium">
                {lotto.numero_lotto ?? '—'}
              </p>
            </div>

            {/* Fornitore */}
            <div>
              <p className="text-muted-foreground">Fornitore</p>
              <p className="font-medium">{fornitoreNome || '—'}</p>
            </div>

            {/* Posizione */}
            <div>
              <p className="text-muted-foreground">Posizione</p>
              <p className="font-medium">{posizioneLabel || '—'}</p>
            </div>

            {/* Quantita' */}
            <div>
              <p className="text-muted-foreground">Disponibile / Iniziale</p>
              <p className="font-mono text-lg font-semibold">
                {lotto.quantita_disponibile} / {lotto.quantita_iniziale}
              </p>
            </div>

            {/* Costo unitario */}
            <div>
              <p className="text-muted-foreground">Costo unitario</p>
              <p className="font-mono font-medium">
                {lotto.costo_unitario != null
                  ? fmt.currency(lotto.costo_unitario)
                  : '—'}
              </p>
            </div>

            {/* Data ingresso */}
            <div>
              <p className="text-muted-foreground">Data ingresso</p>
              <p className="font-medium">{fmt.date(lotto.data_ingresso)}</p>
            </div>

            {/* Data produzione */}
            <div>
              <p className="text-muted-foreground">Data produzione</p>
              <p className="font-medium">
                {lotto.data_produzione ? fmt.date(lotto.data_produzione) : '—'}
              </p>
            </div>

            {/* Data scadenza */}
            <div>
              <p className="text-muted-foreground">Data scadenza</p>
              {lotto.data_scadenza ? (
                <Badge variant={scadenzaVariant(lotto.data_scadenza)}>
                  {fmt.date(lotto.data_scadenza)}
                </Badge>
              ) : (
                <p className="font-medium">—</p>
              )}
            </div>

            {/* Stato */}
            <div>
              <p className="text-muted-foreground">Stato</p>
              <Badge
                variant={
                  lotto.stato === 'disponibile' ? 'default' :
                  lotto.stato === 'in_quarantena' ? 'destructive' : 'secondary'
                }
              >
                {statoLabel(lotto.stato)}
              </Badge>
            </div>

            {/* Created at */}
            <div>
              <p className="text-muted-foreground">Creato il</p>
              <p className="text-sm">{fmt.date(lotto.created_at)}</p>
            </div>

            {/* Updated at */}
            <div>
              <p className="text-muted-foreground">Aggiornato il</p>
              <p className="text-sm">{fmt.date(lotto.updated_at)}</p>
            </div>
          </div>

          {/* Note */}
          {lotto.note && (
            <div className="mt-4">
              <p className="text-muted-foreground">Note</p>
              <p className="text-sm">{lotto.note}</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Movimenti del lotto */}
      <Card>
        <CardHeader>
          <CardTitle>Movimenti</CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <MovimentiTable lottoId={lotto.id} />
        </CardContent>
      </Card>
    </div>
  );
};

export default LottoDetail;
